import {
  ColumnDefinition,
  getDefaultColumnDescription
} from 'components/TableColumn/TableColumn';
import { commaDecorator } from 'components/ReportTable/ReportDataHelper';
import i18n from 'i18n';
import {
  csvHourLabelValueMap,
  hourLabelValueMap,
  KEY_COLUMN
} from './OutdoorDeviceReportModel';
import { OutdoorDeviceReportTableRecord } from 'core/report/ReportData';
import styles from './outdoorDeviceReport.module.scss';
import { DataProvider, OutdoorDeviceReportCsvContent } from './DataProvider';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';

export class DayDataProvider implements DataProvider {
  constructor(protected deliveryUnit?: DeliveryUnit) {}

  protected get deliveryTargetNameHeader(): string {
    return this.deliveryUnit === DeliveryUnit.DEVICE
      ? i18n.t('outdoorDeviceReport.headers.deviceName')
      : i18n.t('outdoorDeviceReport.headers.storeName');
  }

  normalizeData(metrics: Record<string, number>): Record<string, number> {
    const normalized: Record<string, number> = {};
    for (let hour = 0; hour <= 23; hour++) {
      const timeKey = hour.toString();
      normalized[timeKey] = metrics[timeKey] ?? 0;
    }
    return normalized;
  }
  getColumns(
    tableSummaryRecord: OutdoorDeviceReportTableRecord | undefined
  ): ColumnDefinition[] {
    const columns: ColumnDefinition[] = [
      {
        ...getDefaultColumnDescription(
          KEY_COLUMN,
          this.deliveryTargetNameHeader,
          true
        ),
        footer: tableSummaryRecord ? tableSummaryRecord[KEY_COLUMN] : undefined,
        classes: () => styles.storeName,
        headerClasses: () => styles.storeName,
        footerClasses: () => styles.storeName
      }
    ];
    for (let hour = 0; hour <= 23; hour++) {
      const columnKey = hour.toString();
      columns.push({
        ...getDefaultColumnDescription(
          columnKey,
          hourLabelValueMap[columnKey],
          false
        ),
        footer: tableSummaryRecord
          ? commaDecorator(tableSummaryRecord[columnKey])
          : undefined
      });
    }
    return columns;
  }
  getSummaryRecord(
    records: OutdoorDeviceReportTableRecord[]
  ): OutdoorDeviceReportTableRecord {
    const hourTotals: Record<string, number> = {};
    records.forEach((record) => {
      for (let hour = 0; hour <= 23; hour++) {
        const hourKey = hour.toString();
        hourTotals[hourKey] =
          (+hourTotals[hourKey] || 0) + (+record[hourKey] || 0);
      }
    });
    return {
      [KEY_COLUMN]: i18n.t('reportTable.footers.total'),
      ...hourTotals
    } as OutdoorDeviceReportTableRecord;
  }
  getChartData(
    tableSummaryRecord: OutdoorDeviceReportTableRecord | undefined,
    metricType: string
  ) {
    if (!tableSummaryRecord) return [];
    return Object.keys(tableSummaryRecord)
      .filter((key) => key !== KEY_COLUMN)
      .map((key) => ({
        name: hourLabelValueMap[key] || key,
        [metricType]: tableSummaryRecord[key]
      }));
  }
  getCsvContent(
    records: OutdoorDeviceReportTableRecord[]
  ): OutdoorDeviceReportCsvContent {
    const hourKeys = Array.from({ length: 24 }, (_, hour) => hour.toString());
    return {
      headers: [
        this.deliveryTargetNameHeader,
        ...hourKeys.map((hourKey) => csvHourLabelValueMap[hourKey])
      ],
      rows: records.map((record) => [
        record[KEY_COLUMN],
        ...hourKeys.map((hourKey) => record[hourKey])
      ])
    };
  }
}
